import type { Student } from '@services/contribution/contribution.d.ts'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SortFunction = (a: any, b: any) => number
type Data = Record<string, string | number | boolean>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValueExtractionFunction = (obj: any) => string | number | boolean
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValueModifierFunction = (value: any) => string | number | boolean

/**
 * Supersort is an improved sorting helper to sort on arbitrary properties and
 * modify the sortable value as needed without writing custom functions.
 *
 * @param dataArg any[] the array to be sorted
 * @param extractor get the property to sort on - can be the property name or a function to select the value
 * @param modifier change the sortable value before sorting. nullable
 * @param direction sort by ascending or descending. Defaults to 1 (ascending)
 * @returns
 */
export function supersort(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataArg: Array<any>,
  extractor: ValueExtractionFunction | string,
  modifier: ValueModifierFunction | null = null,
  direction = 1
) {
  return dataArg.sort((res01, res02) => {
    let arg01 = typeof extractor === 'string' ? res01[extractor] ?? '' : extractor(res01 as Data)
    let arg02 = typeof extractor === 'string' ? res02[extractor] ?? '' : extractor(res02 as Data)

    if (modifier && typeof modifier === 'function') {
      arg01 = modifier(arg01)
      arg02 = modifier(arg02)
    }

    if (arg01 < arg02) {
      return -1 * direction
    }
    if (arg01 > arg02) {
      return 1 * direction
    }
    return 0
  })
}

/** --------- Extractors -------- */

/** Extract Grade Level into Sortable Value
 * Convert a grade enum into a sortable number.
 * ex: 'daycare' is < pre-k, and k is < 1
 * @param grade GradeLevel
 * @returns Number
 */

export function extractGradeLevel(grade: string) {
  if (!grade) {
    return -10
  }
  grade = grade.toLowerCase()

  switch (grade) {
    case 'multi':
      return -4
    case 'daycare':
      return -3
    case 'prek':
      return -2
    case 'k':
      return -1
    case 'other':
      return 100
    default:
      return Number.parseInt(grade)
  }
}

/** Extract Last Name from Full Name
 * ex: 'John Doe' -> 'Doe John'
 *     'John Doe Smith' -> 'Smith John Doe'
 *     'John Doe-Smith' -> 'Doe-Smith John'
 * @param { name: string } obj - the object to extract the name from
 * @returns string
 */
function extractLastNameFromName({ name }: { name: string }) {
  const names = name.split(' ')
  const lastname = names.pop()

  const sortableName = lastname + ' ' + names.join(' ')
  return sortableName.trim()
}

/**
 * Get the students last name and first name
 * @param student Student
 * @returns string
 */
function extractStudentName(student: Student): string {
  return student.lastName + ' ' + student.firstName
}

// Modifiers
/**
 * convert a string to lowercase for case-insensitve comparison
 * @param val string
 * @returns string
 */
export function toLower(val: string) {
  return val.toLowerCase()
}

/**
 * Convert a string to a number for numeric conversion
 * (otherwise alphanumberic is used by default)
 * @param val string
 * @returns number
 */
export function toNumber(val: string) {
  const n = Number(val)
  if (Number.isNaN(n)) {
    return 0.0
  }
  return n
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sort(data: any[], sortBy: string) {
  if (data === undefined || data.length === 0) {
    return []
  }

  switch (sortBy) {
    case 'GRADE_ASC':
      return supersort(data, 'grade', extractGradeLevel, 1)
    case 'GRADE_DESC':
      return supersort(data, 'grade', extractGradeLevel, -1)

    case 'ID_ASC':
      return supersort(data, 'id', toNumber, 1)
    case 'ID_DESC':
      return supersort(data, 'id', toNumber, -1)

    case 'LASTNAME_EXTRACTED_ASC':
      return supersort(data, extractLastNameFromName, toLower, 1)
    case 'LASTNAME_EXTRACTED_DESC':
      return supersort(data, extractLastNameFromName, toLower, -1)

    case 'NAME_ASC':
      return supersort(data, 'name', toLower, 1)
    case 'NAME_DESC':
      return supersort(data, 'name', toLower, -1)

    case 'STUDENT_ASC':
      return supersort(data, extractStudentName, toLower, 1)
    case 'STUDENT_DESC':
      return supersort(data, extractStudentName, toLower, -1)
    default:
      return data
  }
}
